import { concepts, graphemeById, mappings, wordById, words } from '../data/foundations'
import type { Lesson } from '../types/content'
import { canDecodeAt, shuffled } from './learningJourney'

export function resolveLessonContent(lesson: Lesson): Lesson {
  if (lesson.wordIds.length || lesson.mode === 'oral_only') return lesson
  const concept = concepts.find((item) => item.id === lesson.conceptId)
  // Some existing templates slice beyond their short word list. Reuse existing
  // decodable examples, or existing words for this mapping; never future rules.
  const examples = (concept?.exampleWordIds ?? []).map((id) => wordById.get(id))
    .filter((word) => word && canDecodeAt(word, concept?.order ?? 0)).map((word) => word!.id)
  const fallback = examples.length ? examples : words.filter((word) => canDecodeAt(word, concept?.order ?? 0)
    && word.mapping.some((part) => concept?.mappingIds.includes(part.mappingId)))
    .sort((a, b) => a.difficulty - b.difficulty || a.spelling.localeCompare(b.spelling)).slice(0, 6).map((word) => word.id)
  return fallback.length ? { ...lesson, wordIds: fallback } : lesson
}

export function activityModel(sourceLesson: Lesson) {
  const lesson = resolveLessonContent(sourceLesson)
  const concept = concepts.find((item) => item.id === lesson.conceptId) ?? concepts[0]
  const word = lesson.wordIds.map((id) => wordById.get(id)).find(Boolean)
  const grapheme = graphemeById.get(lesson.targetGraphemeIds[0])
  const learned = concepts.filter((item) => item.order <= concept.order)
  const learnedMappingIds = new Set(learned.flatMap((item) => item.mappingIds))
  const learnedMappings = mappings.filter((mapping) => learnedMappingIds.has(mapping.id))
  const sameSound = new Set(learnedMappings.filter((mapping) => mapping.phonemeIds.join('|') === lesson.targetPhonemeIds.join('|')).map((mapping) => mapping.graphemeId))
  const different = [...new Set(learned.flatMap((item) => item.graphemeIds))].filter((id) => id !== grapheme?.id && !sameSound.has(id))
    .map((id) => graphemeById.get(id)!).filter(Boolean)
  const letters = grapheme ? shuffled([grapheme, ...shuffled(different).slice(0, 2)]) : []
  const wordChoices = word ? shuffled([word, ...shuffled(words.filter((item) => item.id !== word.id && canDecodeAt(item, concept.order) && item.phonemeIds.join('|') !== word.phonemeIds.join('|'))).slice(0, 2)]) : []
  const hunt = grapheme ? shuffled([grapheme, grapheme, ...letters.filter((item) => item.id !== grapheme.id), grapheme]) : []
  const requiredTiles = word?.mapping.map((item) => item.grapheme) ?? []
  const spellingTiles = word ? shuffled([...requiredTiles, ...different.filter((item) => !requiredTiles.includes(item.display)).slice(0, 2).map((item) => item.display)]) : []
  return { concept, word, grapheme, letters, wordChoices, hunt, spellingTiles }
}
